/**
 * FDR Oversight — Main container.
 * Same architecture as PPM: header with horizontal tab navigation.
 * Compliance-only access (superadmin, superuser, admin without department).
 */

import React, { useState, useEffect, useRef } from 'react';
import FDRDashboard from './FDRDashboard';
import CommandPalette from './CommandPalette';
import EntityRegistry from './EntityRegistry';
import EntityDetail from './EntityDetail';
import ContractManager from './ContractManager';
import Assessments from './Assessments';
import FindingsTracker from './FindingsTracker';
import FDRAuditTrail from './FDRAuditTrail';
import OversightCoa from './OversightCoa';

// Reusable tooltip — hover to see plain-English explanation
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  const handleEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: Math.max(8, rect.left + rect.width / 2 - 140) });
    setShow(true);
  };

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)} className="relative inline-flex">
      {children}
      {show && (
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-[280px] px-3 py-2.5 bg-slate-900 text-white text-xs leading-relaxed rounded-xl shadow-xl pointer-events-none">
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-900 rotate-45 rounded-sm" />
          {text}
        </div>
      )}
    </span>
  );
}

// Exportable tooltip for child pages
export { Tooltip };

const TABS = [
  { id: 'dashboard',    label: 'Dashboard',      tip: 'A snapshot of your entire FDR program: where every vendor stands in the assessment cycle, risk breakdown, open findings and CAPs, and what comes due next.',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'entities',     label: 'FDR Registry',    tip: 'Your master list of all vendors, subcontractors, and delegates (FDRs). Add new entities, track their risk level, and see their compliance status.',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { id: 'contracts',    label: 'Contracts',        tip: 'Delegation agreements with your FDR entities. Tracks which CMS-required provisions (like compliance training, reporting mechanisms, and exclusion screening) are included in each contract.',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { id: 'coa',          label: 'COA',              tip: 'FDR oversight work plan and vendor scorecards. Upload each vendor\'s scorecard workbook as it arrives — scores ingest, benchmarks are measured, and misses flag red. Oversight activities are pinned to an FDR and can sync results from Scrubber or Reporter.',
    icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { id: 'assessments',  label: 'Assessments',      tip: 'The annual assessment, vendor by vendor. Your real audit tools assemble automatically from what each vendor is delegated to do; score Met / Partially Met / Not Met with evidence, log findings into CAPs, and close with the PHI destruction certificate on the record.',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { id: 'findings',     label: 'Findings & CAPs',  tip: 'Deficiencies found during assessments or oversight activities. Each finding can have a Corrective Action Plan (CAP) with milestones, due dates, and evidence tracking. Aligns with CMS audit protocol (CAR vs. Observation).',
    icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
  { id: 'audit',        label: 'Audit Trail',      tip: 'A complete, tamper-proof record of every action taken in the FDR module — entity changes, assessments, findings, and more. Retained for 7 years for CMS audit readiness.',
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' },
];

export default function FDRCommandCenter() {
  // My Oversight retired (operator order 2026-08-31): the scorecards and the
  // program tabs carry the work; the Dashboard is the landing view.
  // A deep link names the tab: /app?app=fdr&tab=coa (the Scrubber's "Open in
  // FDR COA" after a sync lands on the work plan, not the dashboard).
  const [view, setView] = useState(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get('tab');
      if (tab && TABS.some((t) => t.id === tab)) return tab;
    } catch (_) { /* no window or no search: the dashboard */ }
    return 'dashboard';
  });
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [showHelp, setShowHelp] = useState(() => !localStorage.getItem('fdr-help-dismissed'));

  const handleOpenEntity = (id) => { setSelectedEntityId(id); setView('entity-detail'); };
  const handleBack = () => { setSelectedEntityId(null); setView('entities'); };

  const handleTabSelect = (tabId) => {
    setView(tabId);
    setSelectedEntityId(null);
  };

  const isDetailView = view === 'entity-detail';

  const renderContent = () => {
    if (view === 'entity-detail' && selectedEntityId) return <EntityDetail entityId={selectedEntityId} onBack={handleBack} onOpenEntity={handleOpenEntity} />;
    if (view === 'entities') return <EntityRegistry onOpenEntity={handleOpenEntity} />;
    if (view === 'contracts') return <ContractManager onOpenEntity={handleOpenEntity} />;
    if (view === 'coa') return <OversightCoa />;
    if (view === 'assessments') return <Assessments onOpenEntity={handleOpenEntity} />;
    if (view === 'findings') return <FindingsTracker onOpenEntity={handleOpenEntity} />;
    if (view === 'audit') return <FDRAuditTrail />;
    return <FDRDashboard onOpenEntity={handleOpenEntity} onNavigate={handleTabSelect} />;
  };

  return (
    // COA: the whole FDR page is the scroll container, so the header scrolls
    // away and the work plan grid gets the window. Every other view keeps
    // the fixed header with its own scrolling main.
    <div className={`h-full flex flex-col bg-[#f8fafc] min-w-0 min-h-0${view === 'coa' ? ' overflow-y-auto' : ''}`}>
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60">
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">FDR Oversight</h1>
              <p className="text-slate-400 text-sm mt-0.5">First-tier, Downstream & Related entity management</p>
            </div>
            <Tooltip text="Learn what FDR Oversight does and how to use each section.">
              <button onClick={() => { setShowHelp(!showHelp); if (showHelp) localStorage.setItem('fdr-help-dismissed', '1'); else localStorage.removeItem('fdr-help-dismissed'); }}
                className="ml-auto flex-shrink-0 w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </Tooltip>
          </div>
          {showHelp && (
            <div className="mt-3 bg-orange-50 border border-orange-100 rounded-xl px-5 py-4 text-sm text-slate-700 leading-relaxed">
              <div className="font-semibold text-slate-800 mb-2">What is FDR Oversight?</div>
              <p className="mb-2">
                If your health plan delegates any function to a vendor or subcontractor (claims processing, credentialing, pharmacy benefits, etc.), CMS requires you to oversee them. These vendors are called <strong>First-tier, Downstream, and Related entities (FDRs)</strong>.
              </p>
              <p className="mb-2">
                This module helps you track every FDR, ensure their contracts include required compliance provisions, assess them against the <strong>7 CMS compliance program elements</strong>, screen their employees against the <strong>OIG exclusion list</strong> monthly, and collect annual compliance attestations.
              </p>
              <p className="text-slate-500">
                <strong>Start here:</strong> Add your FDR entities in the Registry tab, then create their delegation contracts. Run the annual assessments, track findings and CAPs, and keep every audit record in one place. Hover over any tab name for a description of what it does.
              </p>
              <button onClick={() => { setShowHelp(false); localStorage.setItem('fdr-help-dismissed', '1'); }}
                className="mt-3 text-xs font-medium text-orange-600 hover:text-orange-700">Got it, don't show this again</button>
            </div>
          )}
        </div>

        {!isDetailView && (
          <div className="px-8 pb-0">
            <div className="flex gap-0.5 overflow-x-auto scrollbar-hide">
              {TABS.map(tab => (
                <Tooltip key={tab.id} text={tab.tip}>
                  <button
                    onClick={() => handleTabSelect(tab.id)}
                    className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-all rounded-t-xl whitespace-nowrap ${
                      view === tab.id
                        ? 'bg-[#f8fafc] text-orange-700 border border-slate-200/60 border-b-[#f8fafc] -mb-px z-10'
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                    </svg>
                    {tab.label}
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        )}
      </header>

      <CommandPalette tabs={TABS} onNavigate={handleTabSelect} onOpenEntity={handleOpenEntity} />

      <main className={view === 'coa' ? 'flex-1 min-w-0' : 'flex-1 overflow-auto'}>
        {renderContent()}
      </main>
    </div>
  );
}
